// @flow
import { useState, useCallback, } from 'react';
import { useCreateResource } from '../../hoc/CreateResource';
import { useCurrentAccount, useUrlParamValue } from '../../hoc/lib';
import { Panel, PanelHeader, PanelSettingsBar, PanelResourceCreateButtons } from '../../element/Panel';
import { SettingsItem } from '../../element/Setting/Settings';
import { Button, } from '../../element/Button';
import { defaultSnapshotSchedule, validateAccess, validateRetention } from './edit/lib';
import { DatabaseServerLimitWrapper, DatabaseTypePanel } from './edit/DatabaseTypeSelector';
import { DatabaseEngineSelect, } from './edit/DatabaseEngineSelect';
import { ResourceCloudIpEdit, useCreateResourceCloudIps } from '../../common/ResourceCloudIps';
import { NameEditPanel } from '../../element/NameEditPanel';
import { useCreateResourceEditorModal, useEditorDirect, useEditorModal } from '../../common/Editor';
import { DbsAccessControlEdit, DEFAULT_ACCESS } from './edit/DbsAccessControlEdit';
import { ResourceAddViewRoute } from '../../element/ResourceAddViewRoute';
import { DbsMaintenanceEdit, weekdayToLabel, hourToLabel, MaintenanceHint } from './edit/DbsMaintenanceEdit';
import { SnapshotEdit, snapshotDisplay, DbsSnapshotHint } from '../../common/SnapshotEdit';
import { RC_API_REQUEST, RC_CACHED } from '../../../state/resource/type';
import { SnapshotRetentionEdit, DEFAULT_RETENTIONS } from '../../common/SnapshotRetentionEdit';
import { LABELS } from '../../element/ResourceLabels';
import { ZoneSelector } from '../../common/ZoneSelector';

import type { DbsCreateParams, DbsMaintenanceParams } from './type';
import type { SelectedZone } from '../../common/ZoneSelector';
import type { BbDatabaseServer, BbDatabaseServerParams, BbDatabaseType, DbsEngineVersion } from '../../../api/type.dbs';
import type { Match } from 'react-router-dom';
import type { FormErrors } from '../../common/lib';
import type { EditorCreateDef } from '../../common/Editor';
import type { UseStateHook } from 'react-hooks';
import type { SnapshotRetentionValues } from '../../common/SnapshotRetentionEdit';
import type { DbsAccessControlRuleEdit } from "./edit/DbsAccessControlTableRow";

type DbsErrors = { type: ?string, engine: ?string, access_control: FormErrors };

const emptyDbsErrors: DbsErrors = Object.freeze({type: null, engine: null, access_control: new Map<string, string>()});

const maintenanceDef: EditorCreateDef<DbsCreateParams, DbsMaintenanceParams, DbsMaintenanceParams, null> = {
    emptyErrors: null,
    onEdit: (params: DbsCreateParams) => params.maintenance,
    onValidate: null,
    onSave: ([, set]: UseStateHook<DbsCreateParams>, maintenance: DbsMaintenanceParams) => set(p => ({ ...p, maintenance })),
    editUri: 'maintenance',
}
const snapshots_scheduleDef: EditorCreateDef<DbsCreateParams, string, string, null> = {
    emptyErrors: null,
    onEdit: (params: DbsCreateParams) => params.snapshots_schedule,
    onValidate: null,
    onSave: ([, set]: UseStateHook<DbsCreateParams>, snapshots_schedule: string) => set(p => ({ ...p, snapshots_schedule })),
    editUri: 'snapshots_schedule',
}
const snapshots_retentionDef: EditorCreateDef<DbsCreateParams, SnapshotRetentionValues, BbDatabaseServerParams, ?string> = {
    emptyErrors: null,
    editUri: 'snapshots_retention',
    onEdit: (params: DbsCreateParams) => ({
        automatic: Number(params.snapshots_retention) > 0,
        count: params.snapshots_retention || DEFAULT_RETENTIONS,
    }),
    onValidate: (params, value) => {
        const validated = validateRetention(value);
        if (validated[1] && validated[1].snapshots_retention === '') return [null, { snapshots_retention: null, }];
        return validated;
    },
    onSave: ([, set]: UseStateHook<DbsCreateParams>, { snapshots_retention }) => set(p => ({ ...p, snapshots_retention })),
}


export const DbsCreate = ({ match }: { match: Match }): React$Node => {
    const create = useCreateResource<BbDatabaseServer, BbDatabaseServerParams>('dbs_create', 'database_server');
    const cloudIp = useCreateResourceCloudIps();
    const [errors, setErrors] = useState<DbsErrors>(emptyDbsErrors);
    const fromImageMatch = useUrlParamValue(/image=(dbi-[a-z0-9]{5})/);
    const fromImage = fromImageMatch ? fromImageMatch : null;
    const account = useCurrentAccount();

    const createParamsState = useState<DbsCreateParams>({
        name: '',
        description: '',
        access_control: [{ ...DEFAULT_ACCESS }],
        database_type: null,
        source_type: fromImage ? 'snapshot' : 'engine',
        engine: null,
        snapshotFromUrl: fromImageMatch != null,
        snapshot: fromImage,
        maintenance: {
            weekday: 1 + Math.floor(Math.random() * 6),
            hour: Math.floor(Math.random() * 6),
        },
        snapshots_schedule: defaultSnapshotSchedule,
        snapshots_retention: DEFAULT_RETENTIONS,
        zone: null,
    });
    const [createParams, setCreateParams] = createParamsState;

    const maintenance = useCreateResourceEditorModal(createParamsState, maintenanceDef, match.path);
    const snapshots_schedule = useCreateResourceEditorModal(createParamsState, snapshots_scheduleDef, match.path);
    const snapshots_retention = useCreateResourceEditorModal(createParamsState, snapshots_retentionDef, match.path);

    const access_control = useEditorDirect<$ReadOnlyArray<DbsAccessControlRuleEdit>, FormErrors>(
        createParams.access_control,
        (access_control: $ReadOnlyArray<DbsAccessControlRuleEdit>) => setCreateParams((createParams) => ({ ...createParams, access_control })),
        errors.access_control,
    );
    const name = useEditorDirect(
        createParams.name,
        (name: string) => setCreateParams((createParams) => ({...createParams, name})),
        null
    );
    const database_type = useEditorDirect(
        createParams.database_type,
        (database_type: ?BbDatabaseType) => setCreateParams((params: DbsCreateParams) => ({...params, database_type})),
        null
    );
    const zone = useEditorModal<SelectedZone, null, SelectedZone, BbDatabaseServer>(
        null,
        () => createParams.zone || 'auto',
        null,
        (zone: SelectedZone) => setCreateParams({...createParams, zone: zone === 'auto' ? null : zone}),
        match.path,
        'zone'
    );
    // we explicitly store which tab of the engine selector the user
    // is on, because if they choose 'snapshots', but forget to select one,
    // they'll quietly still have an engine selected back on the other tab,
    // which doesn't seem right. so now the tab has to match the selection
    // to validate.
    const onSetSourceType = useCallback(
        (sourceType: $PropertyType<DbsCreateParams, 'source_type'>): void => {
            setCreateParams((createParams: DbsCreateParams) => ({
                ...createParams,
                source_type: sourceType,
            }));
        },
        [setCreateParams]
    );
    const onEngineSelect = useCallback(
        (engine: ?DbsEngineVersion, snapshot: ?string) => setCreateParams((params: DbsCreateParams) => ({
            ...params,
            snapshotFromUrl: false,
            engine: engine ?? params.engine,
            snapshot: snapshot ?? params.snapshot,
            source_type: snapshot == null ? 'engine' : 'snapshot'
        })),
        [setCreateParams]
    );

    const availableMemory = account
        ? account.dbs_ram_limit - account.dbs_ram_used
        : -1; // just pretend there's no limit if we haven't grabbed the account yet.


    const validateAndCreate = () => {
        const {
            access_control: editAccess,
            database_type,
            engine,
            snapshots_schedule,
            maintenance,
            source_type,
            snapshot,
            zone,
            snapshotFromUrl: _snapshotFromUrl,
            ...rest
        } = createParams;

        let [accessErrors, accessCommit] = validateAccess(editAccess);

        let errors = {...emptyDbsErrors};

        if (database_type == null) {
            errors.type = 'Please select a database type.';
        }

        if (engine == null && createParams.snapshot == null) {
            errors.engine = 'Please select a database type.';
        }

        if (source_type === 'engine' && engine == null) {
            errors.engine = 'Please select a database engine.';
        }

        if (source_type === 'snapshot' && snapshot == null) {
            errors.engine = 'Please select a database snapshot.';
        }

        if (accessErrors) {
            errors.access_control = accessErrors;
        }

        setErrors(errors);

        if (errors.type === null && errors.engine === null && accessCommit) {
            const engineParams = source_type === 'engine' && engine ?
                { engine: engine.engine, version: engine.version, }
                : null;

            const zoneParams = zone != null
                ? { zone: zone.id }
                : null;

            const params: BbDatabaseServerParams = {
                ...rest,
                ...engineParams,
                snapshot,
                maintenance_weekday: maintenance.weekday,
                maintenance_hour: maintenance.hour,
                snapshots_schedule: snapshots_schedule === '' ? null : snapshots_schedule,
                database_type: database_type?.id,
                ...zoneParams,
                ...accessCommit,
            };
            create.createResource(params, cloudIp.editor.value);
        }
    };

    const createDisabled =
        createParams.database_type == null
          || (createParams.engine == null && createParams.source_type === 'engine')
          || (createParams.snapshot == null && createParams.source_type === 'snapshot')
        || availableMemory < createParams.database_type.ram
    ;

    return (
        <ResourceAddViewRoute
            listTitle={LABELS.database_server.listTitle}
            match={match}
            dialog={null}
            view={
                <>
                    <DatabaseEngineSelect
                        error={errors.engine}
                        snapshotFromUrl={createParams.snapshotFromUrl}
                        selected={createParams.engine}
                        snapshot={createParams.snapshot}
                        onSelect={onEngineSelect}
                        onSetSourceType={onSetSourceType}
                    />

                    <DatabaseTypePanel
                        editor={database_type}
                        availableMemory={availableMemory}
                    />

                    <ResourceCloudIpEdit
                        edit={cloudIp}
                        resourceId={''}
                        resourceTitle='Cloud SQL'
                    />

                    <DbsAccessControlEdit
                        editor={access_control}
                        cacheStatus={RC_CACHED}
                    />

                    <Panel>
                        <PanelHeader
                            title={'Settings'}
                        />
                        <PanelSettingsBar
                            cacheStatus={RC_CACHED}
                            settings={[
                                {
                                    name: 'Maintenance window',
                                    summary: `${weekdayToLabel[createParams.maintenance.weekday]} between ${hourToLabel[createParams.maintenance.hour]}`,
                                    route: maintenance.editUri,
                                    hint: <MaintenanceHint/>,
                                },
                                {
                                    name: 'Snapshot schedule',
                                    summary: snapshotDisplay(createParams.snapshots_schedule),
                                    route: snapshots_schedule.editUri,
                                    hint: <DbsSnapshotHint/>,
                                },
                                {
                                    name: 'Snapshot Retention',
                                    summary: createParams.snapshots_retention != null
                                        ? 'Keep the  newest ' + Number(createParams.snapshots_retention) + ' snapshots'
                                        : 'Keep all automated snapshots',
                                    route: snapshots_retention.editUri,
                                    hint: <div>The number of automated snapshots to be kept (older snapshots will be deleted)</div>,
                                },
                                {
                                    name: 'Zone Placement',
                                    summary: createParams.zone != null
                                        ? createParams.zone.name
                                        : 'Auto Allocate',
                                    hint: 'Zones high availability by ensuring that your Cloud Servers are located on separate physical hosts',
                                    route: zone.editUri,
                                },
                            ]}
                        >
                            <SettingsItem
                                name={'Admin Password'}
                                summary={
                                    createParams.source_type === 'engine'
                                        ? <i>An auto-generated password will be displayed when the instance is created</i>
                                        : <i>The admin password will be set by the selected database snapshot</i>
                                }
                            />
                        </PanelSettingsBar>
                    </Panel>

                    <NameEditPanel
                        resourceTitle={'Cloud SQL'}
                        editor={name}
                    >
                        <PanelResourceCreateButtons>
                            <DatabaseServerLimitWrapper availableMemory={availableMemory} ram={createParams.database_type?.ram || 0}>
                                <Button
                                    kind='primary'
                                    onClick={validateAndCreate}
                                    state={create.status === RC_API_REQUEST ? 'loading' : ''}
                                    disabled={createDisabled}
                                >
                                    Create Cloud SQL Instance
                                </Button>
                            </DatabaseServerLimitWrapper>
                        </PanelResourceCreateButtons>
                    </NameEditPanel>
                </>
            }
            editors={[
                { editor: maintenance, component: DbsMaintenanceEdit, },
                { editor: snapshots_schedule, render: () => <SnapshotEdit editor={snapshots_schedule} kind='database_server' />, },
                { editor: snapshots_retention, component: SnapshotRetentionEdit, },
                { editor: zone, render: () => <ZoneSelector editor={zone} kind='database_server' />, },
            ]}
        />
    );
};

